import { APICore } from "../helpers/api/apiCore";

const api = new APICore();

const isSupported = (): boolean =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

const serializeSubscription = (sub: PushSubscription) => {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    keys: {
      p256dh: json.keys?.p256dh || "",
      auth: json.keys?.auth || "",
    },
    userAgent: navigator.userAgent,
  };
};

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

  async registerSW(): Promise<ServiceWorkerRegistration | null> {
    if (!isSupported()) return null;

    // Avoid duplicate registration attempts
    if (this.registrationPromise) {
      return this.registrationPromise;
    }

    this.registrationPromise = (async () => {
      try {
        // Scope '/' → served from public/ root. Do NOT move the file.
        // `updateViaCache: 'none'` forces the browser to bypass HTTP cache
        // when checking for a new service-worker.js on each page load —
        // without it, an installed PWA can keep the old SW for 24h after
        // a redeploy.
        this.registration = await navigator.serviceWorker.register(
          "/service-worker.js",
          { scope: "/", updateViaCache: "none" }
        );
        // Kick an update check immediately so users on installed PWAs pick
        // up any newly-deployed SW without waiting for the next reload.
        this.registration.update().catch(() => {});
        console.log("[pushService] SW registered successfully");
        return this.registration;
      } catch (err) {
        console.error("[pushService] SW register failed", err);
        this.registrationPromise = null;
        return null;
      }
    })();

    return this.registrationPromise;
  }

  private async getRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.registration) return this.registration;
    if (!isSupported()) return null;
    this.registration = await navigator.serviceWorker.ready;
    return this.registration;
  }

  /**
   * Subscribe the browser to Web Push and persist the subscription server-side.
   * Assumes Notification permission has already been granted.
   */
  async subscribe(): Promise<boolean> {
    if (!isSupported()) {
      console.warn("[pushService] Push notifications not supported on this browser");
      return false;
    }

    if (Notification.permission !== "granted") {
      console.warn("[pushService] Notification permission not granted");
      return false;
    }

    const vapid = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapid) {
      console.warn("[pushService] REACT_APP_VAPID_PUBLIC_KEY missing in .env");
      return false;
    }

    const reg = await this.getRegistration();
    if (!reg) {
      console.warn("[pushService] Service worker not registered");
      return false;
    }

    try {
      // Check if already subscribed
      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        console.log("[pushService] Already subscribed, updating server-side...");
      } else {
        console.log("[pushService] Creating new push subscription...");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid),
        });
      }

      // Send subscription to server
      const response = await api.create("/push/subscribe", serializeSubscription(sub));
      console.log("[pushService] Subscription sent to server", response);
      return true;
    } catch (err: any) {
      console.error("[pushService] subscribe failed", err?.message || err);
      // Log more detailed error information
      if (err?.response?.data?.error) {
        console.error("[pushService] Server error:", err.response.data.error);
      }
      return false;
    }
  }

  async unsubscribe(): Promise<void> {
    if (!isSupported()) return;
    const reg = await this.getRegistration();
    if (!reg) return;
    try {
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      await api.create("/push/unsubscribe", { endpoint: sub.endpoint });
      await sub.unsubscribe();
    } catch (err) {
      console.error("[pushService] unsubscribe failed", err);
    }
  }
}

export const pushService = new PushNotificationService();
